import axiosInstance from '@/setup/axios/axiosInstance';
import { 
  Supplier, 
  SupplierListResponse, 
  CreateSupplierRequest, 
  UpdateSupplierRequest,
  SupplierSearchParams,
  SupplierStatistics
} from '../models/SupplierModel';

// Get all suppliers
export const getSuppliers = async (params?: SupplierSearchParams): Promise<SupplierListResponse | Supplier[]> => {
  try {
    const response = await axiosInstance.get('/suppliers/', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

// Get supplier by ID
export const getSupplierById = async (id: string): Promise<Supplier> => {
  try {
    const response = await axiosInstance.get(`/suppliers/${id}/`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching supplier ${id}:`, error);
    throw error;
  }
};

// Create new supplier
export const createSupplier = async (supplierData: CreateSupplierRequest): Promise<Supplier> => {
  try {
    const response = await axiosInstance.post('/suppliers/', supplierData);
    return response.data;
  } catch (error) {
    console.error('Error creating supplier:', error);
    throw error;
  }
};

// Update supplier
export const updateSupplier = async (id: string, supplierData: UpdateSupplierRequest): Promise<Supplier> => {
  try {
    const response = await axiosInstance.put(`/suppliers/${id}/`, supplierData);
    return response.data;
  } catch (error) {
    console.error(`Error updating supplier ${id}:`, error);
    throw error;
  }
};

// Delete supplier
export const deleteSupplier = async (id: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/suppliers/${id}/`);
  } catch (error) {
    console.error(`Error deleting supplier ${id}:`, error);
    throw error;
  }
};

// Search suppliers
export const searchSuppliers = async (params: SupplierSearchParams): Promise<SupplierListResponse> => {
  try {
    const response = await axiosInstance.get('/suppliers/search/', { params });
    return response.data;
  } catch (error) {
    console.error('Error searching suppliers:', error);
    throw error;
  }
};

// Get supplier statistics
export const getSupplierStatistics = async (): Promise<SupplierStatistics> => {
  try {
    const response = await axiosInstance.get('/suppliers/statistics/');
    return response.data;
  } catch (error) {
    console.error('Error fetching supplier statistics:', error);
    throw error;
  }
};

// Bulk update suppliers
export const bulkUpdateSuppliers = async (data: { 
  ids: string[]; 
  updates: Partial<UpdateSupplierRequest> 
}): Promise<{ message: string; updated_count: number }> => {
  try {
    const response = await axiosInstance.post('/suppliers/bulk_update/', data);
    return response.data;
  } catch (error) {
    console.error('Error bulk updating suppliers:', error);
    throw error;
  }
};

// Add supplier to company
export const addSupplierToCompany = async (supplierId: string, companyId: string): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.post(`/suppliers/${supplierId}/add_to_company/`, { 
      company_id: companyId 
    });
    return response.data;
  } catch (error) {
    console.error(`Error adding supplier ${supplierId} to company ${companyId}:`, error);
    throw error;
  }
};

// Remove supplier from company
export const removeSupplierFromCompany = async (supplierId: string, companyId: string): Promise<{ message: string }> => {
  try {
    const response = await axiosInstance.post(`/suppliers/${supplierId}/remove_from_company/`, { 
      company_id: companyId 
    });
    return response.data;
  } catch (error) {
    console.error(`Error removing supplier ${supplierId} from company ${companyId}:`, error);
    throw error;
  }
};

// Get suppliers by company
export const getSuppliersByCompany = async (companyId: string, params?: SupplierSearchParams): Promise<SupplierListResponse> => {
  try {
    const response = await axiosInstance.get('/suppliers/', {
      params: {
        ...params,
        company_id: companyId,
      },
    });
    return response.data;
  } catch (error) {
    console.error(`Error fetching suppliers for company ${companyId}:`, error);
    throw error;
  }
};

// Export suppliers
export const exportSuppliers = async (params?: SupplierSearchParams): Promise<Blob> => {
  try {
    const response = await axiosInstance.get('/suppliers/export/', { 
      params,
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    console.error('Error exporting suppliers:', error);
    throw error;
  }
};

// Import suppliers
export const importSuppliers = async (file: File): Promise<{ message: string; imported_count: number; errors?: string[] }> => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await axiosInstance.post('/suppliers/import/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error importing suppliers:', error);
    throw error;
  }
};

// Soft delete suppliers
export const softDeleteSuppliers = async (supplierIds: string[]) => {
  try {
    const response = await axiosInstance.post("/suppliers/soft_delete/", {
      supplier_ids: supplierIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Restore deleted suppliers
export const restoreSuppliers = async (supplierIds: string[]) => {
  try {
    const response = await axiosInstance.post("/suppliers/restore_deleted/", {
      supplier_ids: supplierIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Permanently delete suppliers
export const permanentDeleteSuppliers = async (supplierIds: string[]) => {
  try {
    const response = await axiosInstance.post("/suppliers/permanent_delete/", {
      supplier_ids: supplierIds,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Get deleted suppliers
export const getDeletedSuppliers = async () => {
  try {
    const response = await axiosInstance.get("/suppliers/?show_deleted=true");
    return response;
  } catch (error) {
    throw error;
  }
};
